import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Upload,
  InputNumber,
  Button,
  message,
  Modal,
} from 'antd';
import { UploadOutlined, FileSearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useSocketContext } from '../../../sockets/SocketContext';
import { setLastImageForReport, setLastSearchQuery, setFormValues } from '../../../store/tz/reducer';
import ImageCropper from '../ext/ImageCropper';
import MktuClassSelector from '../../common/MktuClassSelector';

/** Предобработка: макс. 946px, макс. 2 MB */
const MAX_DIMENSION = 946;
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB (результат после сжатия)
const MAX_INPUT_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB (макс. размер входа)

const preprocessImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => (img.src = e.target.result);
    img.onload = () => {
      const k = Math.min(MAX_DIMENSION / img.width, MAX_DIMENSION / img.height, 1);
      const w = Math.floor(img.width * k);
      const h = Math.floor(img.height * k);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      const tryCompress = (quality) =>
        new Promise((res, rej) => {
          canvas.toBlob(
            (blob) =>
              blob
                ? res(
                    new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                      type: 'image/jpeg',
                      lastModified: Date.now(),
                    }),
                  )
                : rej(new Error('Не удалось сконвертировать')),
            'image/jpeg',
            quality,
          );
        });

      const compressWithQuality = async (quality) => {
        const result = await tryCompress(quality);
        if (result.size <= MAX_FILE_SIZE_BYTES || quality <= 0.3) {
          return result;
        }
        const nextQuality = Math.max(0.3, quality - 0.15);
        return compressWithQuality(nextQuality);
      };

      tryCompress(0.92)
        .then((result) => {
          if (result.size <= MAX_FILE_SIZE_BYTES) {
            resolve(result);
          } else {
            compressWithQuality(0.85).then(resolve).catch(reject);
          }
        })
        .catch(reject);
    };
    img.onerror = () => reject(new Error('Ошибка загрузки'));
    reader.readAsDataURL(file);
  });

/** Сжимает Blob или data URL до лимитов и возвращает base64 для отправки */
const getCompressedBase64 = async (blobOrDataUrl) => {
  let blob;
  if (blobOrDataUrl instanceof Blob) {
    blob = blobOrDataUrl;
  } else {
    const res = await fetch(blobOrDataUrl);
    blob = await res.blob();
  }
  const file = new File([blob], 'image.jpg', { type: blob.type || 'image/jpeg' });
  const compressed = await preprocessImage(file);
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(compressed);
  });
};

const SearchForm = ({ setLocalState }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const savedValues = useSelector((s) => s.tz.forms.image);
  const { createNewTaskOnServer, socket } = useSocketContext();

  const [previewSrc, setPreviewSrc] = useState(null);
  const [originalPreviewSrc, setOriginalPreviewSrc] = useState(null);
  const [cropVisible, setCropVisible] = useState(false);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [cropConfig, setCropConfig] = useState(null);
  const [fileName, setFileName] = useState(null);

  // Состояния для замера времени
  const [searchStartTime, setSearchStartTime] = useState(null);
  const [tzTime, setTzTime] = useState(null);
  const [requestsTime, setRequestsTime] = useState(null);
  const [showTimePanel, setShowTimePanel] = useState(false);
  const [hidePanelTimeout, setHidePanelTimeout] = useState(null);
  const [hoverTestBtn, setHoverTestBtn] = useState(false);

  useEffect(() => {
    dispatch(setFormValues({ formKey: 'image', values: { fileName: fileName || '' } }));
  }, [fileName, dispatch]);

  /** В Redux — то же изображение, что в превью (в т.ч. после кропа), а не только исходник при загрузке */
  useEffect(() => {
    dispatch(setLastImageForReport(previewSrc || null));
  }, [previewSrc, dispatch]);

  /** Сохраняем стейт наверх, чтобы Results могли открыть modal */
  const syncStateUp = (extra = {}) =>
    setLocalState({
      previewSrc,
      originalPreviewSrc,
      croppedBlob,
      cropConfig,
      fileName,
      ...extra,
    });

  /** Загрузка изображения */
  const handleFileChange = async ({ fileList }) => {
    if (!fileList.length) {
      resetFile();
      return;
    }
    let file = fileList[fileList.length - 1].originFileObj;
    const originalSize = file.size;
    try {
      file = await preprocessImage(file);
      if (file.size < originalSize) {
        message.info('Изображение сжато до допустимого размера');
      }
    } catch (err) {
      message.error(err?.message || 'Ошибка обработки изображения');
      resetFile();
      return;
    }
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataURL = e.target.result;
      setOriginalPreviewSrc(dataURL);
      setPreviewSrc(dataURL);
      setCropVisible(true);
      syncStateUp({ originalPreviewSrc: dataURL, previewSrc: dataURL });
    };
    reader.readAsDataURL(file);
  };

  const resetFile = () => {
    setPreviewSrc(null);
    setOriginalPreviewSrc(null);
    setCroppedBlob(null);
    setCropConfig(null);
    setFileName(null);
    syncStateUp({
      previewSrc: null,
      originalPreviewSrc: null,
      croppedBlob: null,
      cropConfig: null,
      fileName: null,
    });
  };

  /** Отправка формы */
  // const onFinish = ({ quantity }) => {
  //   if (!previewSrc && !croppedBlob) {
  //     message.error('Сначала выберите изображение');
  //     return;
  //   }

  //   const send = (base64) => {
  //     // Сохраняем поисковый запрос (имя файла как запрос)
  //     dispatch(setLastSearchQuery(fileName || 'Поиск по изображению'));
      
  //     /** Создаём сразу две задачи */
  //     const common = { image: base64, quantity, imageName: fileName ,vectors: "tz_vectors" };
  //     const common2 = { imageReq: base64, quantity, imageName: fileName , vectors: "requests_vectors"};

  //     createNewTaskOnServer(common, 'image', (err) =>
  //       err
  //         ? message.error('Не удалось создать задачу ТЗ')
  //         : message.success('Задача ТЗ создана'),
  //     );
  //     createNewTaskOnServer(common2, 'imageReq', (err) =>
  //       err
  //         ? message.error('Не удалось создать задачу заявок')
  //         : message.success('Задача заявок создана'),
  //     );
  //   };

  //   if (croppedBlob) {
  //     const reader = new FileReader();
  //     reader.onload = (e) => send(e.target.result.split(',')[1]);
  //     reader.readAsDataURL(croppedBlob);
  //   } else {
  //     send(previewSrc.split(',')[1]);
  //   }
  // };

  useEffect(() => {
    form.setFieldsValue(savedValues);
  }, [form, savedValues]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (hidePanelTimeout) {
        clearTimeout(hidePanelTimeout);
      }
    };
  }, [hidePanelTimeout]);

  // Функция для обработки завершения задачи
  const handleTaskComplete = useCallback((taskType) => {
    if (searchStartTime) {
      const endTime = Date.now();
      const timeDiff = endTime - searchStartTime;

      if (taskType === 'tz') {
        setTzTime(timeDiff);
      } else if (taskType === 'requests') {
        setRequestsTime(timeDiff);
      } 
    }
  }, [searchStartTime]);

  // Функции для управления панелью времени
  const showTimePanelHandler = () => {
    if (hidePanelTimeout) {
      clearTimeout(hidePanelTimeout);
      setHidePanelTimeout(null);
    }
    setShowTimePanel(true);
  };

  const hideTimePanelHandler = () => {
    const timeout = setTimeout(() => {
      setShowTimePanel(false);
    }, 150); // Задержка 150мс перед скрытием
    setHidePanelTimeout(timeout);
  };

  // Функция для сброса времени
  const resetResponseTime = () => {
    setTzTime(null);
    setRequestsTime(null);
    setShowTimePanel(false);
    setSearchStartTime(null);
    if (hidePanelTimeout) {
      clearTimeout(hidePanelTimeout);
      setHidePanelTimeout(null);
    }
  };

  // Прослушиваем завершение задач
  useEffect(() => {
    if (!socket) return;

    const handleSubtaskUpdate = (update) => {
      const { taskInfo } = update;

      // Определяем тип задачи и фиксируем время
      if (taskInfo.request_type === 'image') {
        if (taskInfo.status === 'completed' || taskInfo.status === 'success' || taskInfo.status === 'error') {
          // Проверяем описание задачи для определения типа
          if (taskInfo.description?.test1) {
            handleTaskComplete('test1');
          } else if (taskInfo.description?.test2) {
            handleTaskComplete('test2');
          } else {
            handleTaskComplete('tz');
          }
        }
      } else if (taskInfo.request_type === 'imageReq') {
        if (taskInfo.status === 'completed' || taskInfo.status === 'success' || taskInfo.status === 'error') {
          handleTaskComplete('requests');
        }
      }
    };

    socket.on('subtask_update', handleSubtaskUpdate);

    return () => {
      socket.off('subtask_update', handleSubtaskUpdate);
    };
  }, [socket, searchStartTime, handleTaskComplete]);

  /** Отправка тестовой задачи (новая логика для кнопки "Найти") */
  const onFindTest = async ({ quantity, mktu }) => {
    if (!previewSrc && !croppedBlob) {
      message.error('Сначала выберите изображение');
      return;
    }

    // Сбрасываем предыдущее время и начинаем новый замер
    resetResponseTime();
    setSearchStartTime(Date.now());

    const sendTest = async (base64) => {
      dispatch(setLastSearchQuery(fileName || 'Поиск по изображению'));
      const common = { image: base64, quantity, imageName: fileName, vectors: "tz_vectors", mktu, test: true, mode: 'with_inversed' };
      const common2 = { imageReq: base64, quantity, imageName: fileName, vectors: "requests_vectors", mktu, test: true, mode: 'with_inversed' };
      const common3 = { imageWipo: base64, quantity, imageName: fileName, vectors: "wipo_vectors", mktu, test: true, mode: 'with_inversed' };

      const jobs = [
        { desc: common, type: 'image', label: 'ТЗ' },
        { desc: common2, type: 'imageReq', label: 'заявок' },
        { desc: common3, type: 'imageWipo', label: 'WIPO' },
      ];

      const results = await Promise.all(
        jobs.map((j) => new Promise((resolve) => {
          createNewTaskOnServer(j.desc, j.type, (err) => resolve({ label: j.label, err }));
        })),
      );
      const failed = results.filter((r) => r.err);
      if (failed.length === 0) {
        message.success('Все запросы отправлены');
      } else {
        failed.forEach((r) => message.error(`Не удалось создать задачу ${r.label}`));
      }
    };
    try {
      const base64 = await getCompressedBase64(croppedBlob || previewSrc);
      await sendTest(base64);
    } catch (err) {
      message.error(err?.message || 'Ошибка сжатия изображения');
    }
  };

  /** Отправка тестовой задачи заявок */
  const onTest = async () => {
    const mktu = form.getFieldValue('mktu');
    if (!previewSrc && !croppedBlob) {
      message.error('Сначала выберите изображение');
      return;
    }
    const sendTest = (base64) => {
      const common = { image: base64, quantity: form.getFieldValue('quantity'), imageName: fileName, vectors: "tz_vectors", test: true, test1: true, mktu };
      createNewTaskOnServer(common, 'image', (err) =>
        err
          ? message.error('Не удалось создать тестовую задачу ')
          : message.success('Тестовая задача заявок создана'),
      );
    };
    try {
      const base64 = await getCompressedBase64(croppedBlob || previewSrc);
      sendTest(base64);
    } catch (err) {
      message.error(err?.message || 'Ошибка сжатия изображения');
    }
  };
  /** Отправка тестовой задачи заявок */
  const onTest2 = async () => {
    const mktu = form.getFieldValue('mktu');
    if (!previewSrc && !croppedBlob) {
      message.error('Сначала выберите изображение');
      return;
    }
    const sendTest = (base64) => {
      const common = { image: base64, quantity: form.getFieldValue('quantity'), imageName: fileName, vectors: "tz_vectors", test: true, test2: true, mktu };
      createNewTaskOnServer(common, 'image', (err) =>
        err
          ? message.error('Не удалось создать тестовую задачу ')
          : message.success('Тестовая задача заявок создана'),
      );
    };
    try {
      const base64 = await getCompressedBase64(croppedBlob || previewSrc);
      sendTest(base64);
    } catch (err) {
      message.error(err?.message || 'Ошибка сжатия изображения');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <Form
          form={form}
          layout="inline"
          onFinish={onFindTest}
          onValuesChange={(_, all) => dispatch(setFormValues({ formKey: 'image', values: all }))}
          style={{ flexWrap: 'wrap', gap: 16, marginBottom: 16, flex: 1 }}
        >
          <Form.Item label="Изображение">
            <Upload
              beforeUpload={(file) => {
                if (file.size > MAX_INPUT_SIZE_BYTES) {
                  message.error(`Файл слишком большой (макс. ${MAX_INPUT_SIZE_BYTES / 1024 / 1024} MB)`);
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={handleFileChange}
              onRemove={resetFile}
              maxCount={1}
              accept="image/*"
              listType="picture-card"
              showUploadList={false}
            >
              {!previewSrc && (
                <div>
                  <UploadOutlined style={{ fontSize: 24 }} />
                  <div style={{ marginTop: 8 }}>Выбрать</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          {previewSrc && (
            <div style={{ position: 'relative', display: 'inline-block', marginRight: 16 }}>
              <img
                src={previewSrc}
                alt="prev"
                onClick={() => setCropVisible(true)}
                style={{
                  maxWidth: 100,
                  maxHeight: 100,
                  objectFit: 'contain',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  cursor: 'pointer',
                  display: 'block',
                }}
              />
              <CloseCircleOutlined
                style={{
                  fontSize: 22,
                  color: '#ff4d4f',
                  cursor: 'pointer',
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  background: 'var(--theme-surface)',
                  borderRadius: '50%',
                  boxShadow: '0 0 2px #ccc',
                }}
                title="Убрать изображение"
                onClick={resetFile}
              />
            </div>
          )}

          <Form.Item
            name="quantity"
            label="Количество"
            initialValue={20}
            rules={[
              { required: true, message: 'Введите количество' },
              { type: 'number', min: 1, max: 200 },
            ]}
          >
            <InputNumber min={1} max={200} style={{ width: '100%', maxWidth: 120, minWidth: 72 }} />
          </Form.Item>

          <Form.Item name="mktu" label="МКТУ">
            <MktuClassSelector placeholder="Классы" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<FileSearchOutlined />}
            >
              Найти
            </Button>
          </Form.Item>

          {/* <Form.Item>
            <Button
              type="default"
              onClick={() => {
                const quantity = form.getFieldValue('quantity');
                onFinish({ quantity });
              }}
              icon={<FileSearchOutlined />}
            >
              Найти 2
            </Button>
          </Form.Item> */}
          </Form>

          {/* Панель со временем всех запросов */}
          <div
            style={{
              marginRight: 16,
              padding: '8px 12px',
              backgroundColor: 'var(--theme-success-bg)',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
              fontSize: 11,
              color: '#52c41a',
              opacity: showTimePanel ? 1 : 0,
              transition: 'opacity 0.3s',
              cursor: 'default',
              minWidth: 180,
            }}
            onMouseEnter={showTimePanelHandler}
            onMouseLeave={hideTimePanelHandler}
          >
            <div>Т: {tzTime ? `${(tzTime / 1000).toFixed(2)}s` : '-'}</div>
            <div>З: {requestsTime ? `${(requestsTime / 1000).toFixed(2)}s` : '-'}</div>
          </div>

          <Button
          type="default"
          style={{
            marginLeft: 'auto',
            marginBottom: 16,
            opacity: hoverTestBtn ? 1 : 0,
            transition: 'opacity 0.3s',
          }}
          onClick={onTest}
          onMouseEnter={() => setHoverTestBtn(true)}
          onMouseLeave={() => setHoverTestBtn(false)}
        >
          Тест1
        </Button>
        <Button
          type="default"
          style={{
            marginLeft: 'auto',
            marginBottom: 16,
            opacity: hoverTestBtn ? 1 : 0,
            transition: 'opacity 0.3s',
          }}
          onClick={onTest2}
          onMouseEnter={() => setHoverTestBtn(true)}
          onMouseLeave={() => setHoverTestBtn(false)}
        >
          Тест2
        </Button>
      </div>

      {/* Модалка кропа */}
      <Modal
        open={cropVisible}
        footer={null}
        onCancel={() => setCropVisible(false)}
        width="min(800px, calc(100vw - 32px))"
        centered
      >
        {originalPreviewSrc && (
          <ImageCropper
            src={originalPreviewSrc}
            initialCrop={cropConfig}
            onComplete={(blob, cfg) => {
              setCroppedBlob(blob);
              setCropConfig(cfg);
              const r = new FileReader();
              r.onload = (e) => {
                setPreviewSrc(e.target.result);
                syncStateUp({
                  previewSrc: e.target.result,
                  croppedBlob: blob,
                  cropConfig: cfg,
                });
              };
              r.readAsDataURL(blob);
              setCropVisible(false);
            }}
            onCancel={() => setCropVisible(false)}
          />
        )}
      </Modal>
    </>
  );
};

export default SearchForm;
